import { createElement } from 'react';
import type { NavigationItem, NavigationGroup } from '@solidops/ui/navigation-menu';
import { NavigationIcon } from '../../assets/icons/NavigationIcon.js';
import type { SidebarGroup } from '@solidops/ui/sidebar';
export function settingsGroups(role: 'Admin' | 'Agent', pathname: string): SidebarGroup[] {
  const item = (href: string, label: string) => ({
    href,
    label,
    current: pathname === href || pathname.startsWith(href + '/'),
  });
  return [
    { id: 'personal', label: 'Personal', items: [item('/settings/contacts', 'Contact methods')] },
    ...(role === 'Admin'
      ? [
          {
            id: 'administration',
            label: 'Administration',
            items: [
              item('/settings/users', 'Users and roles'),
              item('/settings/access/sessions', 'Everyone’s sessions'),
              item('/settings/customers', 'Customers'),
              item('/settings/webhooks', 'Webhooks'),
              item('/settings/notification-policies', 'Notification policies'),
            ],
          },
          {
            id: 'audit',
            label: 'Audit logs',
            items: [
              item('/settings/audit/authentication', 'Login and access activity'),
              item('/settings/audit/customer-activity', 'Customer activity'),
            ],
          },
        ]
      : []),
  ];
}
export function teamGroups(
  teamId: string,
  role: 'Admin' | 'Agent',
  pathname: string,
): SidebarGroup[] {
  const base = '/teams/' + teamId;
  const item = (path: string, label: string) => ({
    href: base + '/' + path,
    label,
    current: pathname === base + '/' + path || pathname.startsWith(base + '/' + path + '/'),
  });
  return [
    {
      id: 'team',
      items: [
        item('schedules', 'Schedules'),
        ...(role === 'Admin'
          ? [
              item('escalations', 'Escalations'),
              item('webhooks', 'Webhooks'),
              item('members', 'Members'),
              item('settings', 'Settings'),
            ]
          : []),
      ],
    },
  ];
}

function menuGroups(groups: readonly SidebarGroup[]): NavigationGroup[] {
  return groups.map((group) => ({
    id: group.id,
    ...(group.label ? { label: group.label } : {}),
    items: group.items.map((item) => ({
      id: item.href,
      label: item.label,
      href: item.href,
      icon: createElement(NavigationIcon, { name: item.label }),
    })),
  }));
}

export function applicationNavigation(
  role: 'Admin' | 'Agent' | null,
  pathname: string,
  search = '',
): NavigationItem[] {
  const dashboard: NavigationItem = {
    type: 'link',
    id: 'dashboard',
    label: 'Dashboard',
    href: '/',
  };
  if (!role) return [dashboard];
  const teamId = pathname.split('/')[1] === 'teams' ? pathname.split('/')[2] : undefined;
  const teams: NavigationItem = teamId
    ? {
        type: 'menu',
        id: 'teams',
        label: 'Teams',
        groups: [
          {
            id: 'directory',
            items: [{ id: 'all-teams', label: 'All teams', href: '/teams', exact: true }],
          },
          ...menuGroups(teamGroups(teamId, role, pathname)),
        ],
      }
    : { type: 'link', id: 'teams', label: 'Teams', href: '/teams' };
  return [
    dashboard,
    {
      type: 'link',
      id: 'incidents',
      label: 'Incidents',
      href:
        pathname === '/incidents' || pathname.startsWith('/incidents/')
          ? `/incidents?view=${new URLSearchParams(search).get('view') === 'all' ? 'all' : 'active'}`
          : '/incidents',
    },
    teams,
    { type: 'link', id: 'on-call', label: 'On-call', href: '/on-call' },
    {
      type: 'menu',
      id: 'settings',
      label: 'Settings',
      groups: menuGroups(settingsGroups(role, pathname)),
    },
  ];
}
